import { isEqual, omitBy, pick } from "lodash";
import { useCallback, useEffect, useMemo } from "react";
import {
  createSearchParams,
  NavigateOptions,
  useSearchParams
} from "react-router-dom";
import { SearchParams } from "../@types";
import { LOCATION } from "../consts";
import { NavigateProps } from "../services/@types";
import { constructSelections, createFilterChangeSelections } from "../util";
import { createQueryParams } from "./utils";
import { useReducedSystemHeathStatus } from "../../header/system-health/hooks/useSystemHealthStatus";

const useSearchNavigation = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isCloudDisabled } = useReducedSystemHeathStatus();

  const searchQueryParams: Partial<SearchParams> = useMemo(
    () => createQueryParams(searchParams, isCloudDisabled),
    [isCloudDisabled, searchParams]
  );

  const navigate = useCallback(
    (
      { isRecent = true, ...props }: NavigateProps,
      navigateOpts?: NavigateOptions
    ) => {
      const selectionsParam = createSearchParams(
        constructSelections(props.selections || searchQueryParams.selections)
      ).toString();

      const newSort = props.sort
        ? `${props.sort.field} ${props.sort.direction}`
        : `${searchQueryParams.sort?.field} ${searchQueryParams.sort?.direction}`;

      const params = {
        location:
          props.location || searchQueryParams.location || LOCATION.LOCAL,
        page: String(props.page || searchQueryParams.page),
        pagesize: String(props.pagesize || searchQueryParams.pagesize),
        sort: newSort,
        bulk: props.isBulk ? String(props.isBulk) : "",
        recent: String(Boolean(isRecent)),
        q: props.q ?? (searchQueryParams.q || ""),
        selections: selectionsParam
      };

      setSearchParams(
        createSearchParams(
          props.isBulk
            ? pick(params, ["location", "bulk", "page", "pagesize"])
            : omitBy(params, value => !value)
        ),
        navigateOpts ?? {}
      );
    },
    [searchQueryParams]
  );

  useEffect(() => {
    const filteredSelections = createFilterChangeSelections(
      searchQueryParams.location || LOCATION.LOCAL,
      searchQueryParams.selections
    );

    if (
      !searchQueryParams.isBulk &&
      !isEqual(searchQueryParams.selections, filteredSelections)
    ) {
      navigate({
        location: searchQueryParams.location,
        selections: filteredSelections
      });
    }
  }, [
    navigate,
    searchQueryParams.isBulk,
    searchQueryParams.location,
    searchQueryParams.selections
  ]);

  return {
    params: searchQueryParams as SearchParams,
    navigate
  };
};

export default useSearchNavigation;
